import FabricAnalyticsListeners, { FabricChannel } from '@atlaskit/analytics-listeners';
import type { AnalyticsWebClient } from '@atlaskit/analytics-listeners';
import { AppSwitcher as AppSwitcherComponent } from '@atlaskit/atlassian-navigation';
import Popup from '@atlaskit/popup';
import RemoteSwitcherPlugin from '@atlassian/remote-module-loader/plugin-switcher';
import { RemoteComponent } from '@atlassian/remote-module-loader/remote-component';
import AtlassianSwitcher, {
	AtlassianSwitcherPrefetchTrigger,
	SwitchToTooltipText,
} from '@atlassian/switcher';
import { useTenantData } from '@dev-agents/tenant-context';
import React from 'react';

import { envType } from '../../../common/types';
import { getEnvType } from '../../../utils';
import { useAnalyticsWebClient } from '../../../utils/analytics/context';

type SwitcherComponentProps = React.ComponentProps<typeof AtlassianSwitcher>;

const product = 'dev-agents';

export const AppSwitcher = () => {
	const { tenantContexts } = useTenantData();
	const { cloudId } = tenantContexts[0];
	const [isOpen, setIsOpen] = React.useState(false);
	const toggleAppSwitcher = React.useCallback(() => {
		setIsOpen((prevState) => !prevState);
	}, []);
	const closeAppSwitcher = React.useCallback(() => setIsOpen(false), []);
	const analyticsClient = useAnalyticsWebClient() as unknown as AnalyticsWebClient;
	const env = getEnvType();
	const remoteSwitcherPlugin = new RemoteSwitcherPlugin({
		env: env === envType.PROD ? 'production' : 'staging',
		variant: 'latest',
	});

	return (
		<FabricAnalyticsListeners
			client={analyticsClient}
			excludedChannels={[
				FabricChannel.atlaskit,
				FabricChannel.elements,
				FabricChannel.editor,
				FabricChannel.media,
			]}
		>
			<Popup
				placement="bottom-start"
				rootBoundary="document"
				onClose={closeAppSwitcher}
				isOpen={isOpen}
				content={() => (
					<RemoteComponent<SwitcherComponentProps>
						remotePlugin={remoteSwitcherPlugin}
						analyticsChannel="navigation"
						ErrorFallback={<AtlassianSwitcher product={product} cloudId={cloudId} />}
						product={product}
						cloudId={cloudId}
					/>
				)}
				trigger={(triggerProps) => (
					<AtlassianSwitcherPrefetchTrigger cloudId={cloudId} product={product}>
						<AppSwitcherComponent
							tooltip={SwitchToTooltipText}
							onClick={toggleAppSwitcher}
							{...triggerProps}
						/>
					</AtlassianSwitcherPrefetchTrigger>
				)}
			/>
		</FabricAnalyticsListeners>
	);
};
